import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  GraduationCap, Clock, Award, CheckCircle2, Play, 
  AlertCircle, Video, BookOpen, Trophy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Função para extrair URL de vídeo do texto
const extractVideoUrl = (text) => {
  if (!text) return null;
  
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)?/i,
    /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)(\d+)(?:[^\s]*)?/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const fullUrl = match[0];
      
      if (fullUrl.includes('youtube') || fullUrl.includes('youtu.be')) {
        return {
          type: 'youtube',
          videoId: match[1],
          embedUrl: `https://www.youtube.com/embed/${match[1]}`
        };
      }
      
      if (fullUrl.includes('vimeo')) {
        return {
          type: 'vimeo',
          videoId: match[1],
          embedUrl: `https://player.vimeo.com/video/${match[1]}`
        };
      }
    }
  }
  
  return null;
};

// Componente para exibir vídeo responsivo
const VideoPlayer = ({ videoInfo }) => {
  if (!videoInfo) return null;
  
  return (
    <div className="mb-4">
      <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-slate-900">
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={videoInfo.embedUrl}
          title="Video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

const MeusTreinamentos = () => {
  const { user } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, points: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API_URL}/employee-portal/my-trainings`);
      setTrainings(response.data.trainings || []);
      setStats(response.data.stats || { total: 0, completed: 0, pending: 0, points: 0 });
    } catch (error) {
      console.error('Erro ao carregar treinamentos:', error);
      toast.error('Erro ao carregar seus treinamentos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'concluido':
        return <Badge className="bg-green-500"><CheckCircle2 size={12} className="mr-1" /> Concluído</Badge>;
      case 'em_andamento':
        return <Badge className="bg-blue-500"><Play size={12} className="mr-1" /> Em Andamento</Badge>;
      case 'nao_iniciado':
        return <Badge className="bg-slate-500"><Clock size={12} className="mr-1" /> Não Iniciado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meus-treinamentos-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Meus Treinamentos
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Olá, {user?.name || 'Funcionário'}! Acompanhe seus treinamentos e DDS.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BookOpen size={24} />
              <div>
                <p className="text-sm opacity-80">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={24} />
              <div>
                <p className="text-sm opacity-80">Concluídos</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={24} />
              <div>
                <p className="text-sm opacity-80">Pendentes</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Trophy size={24} />
              <div>
                <p className="text-sm opacity-80">Pontos</p>
                <p className="text-2xl font-bold">{stats.points}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Training List */}
      {trainings.length > 0 ? (
        <div className="grid gap-4">
          {trainings.map(training => {
            const videoInfo = extractVideoUrl(training.descricao) || 
                             (training.conteudo && typeof training.conteudo === 'string' && extractVideoUrl(training.conteudo));
            
            return (
              <Card 
                key={training.enrollment_id || training.id} 
                className={`hover:shadow-md transition-shadow ${training.tipo === 'dds' ? 'border-l-4 border-l-amber-500' : ''}`}
              >
                <CardContent className="p-4">
                  {/* Video Player */}
                  {videoInfo && <VideoPlayer videoInfo={videoInfo} />}
                  
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-semibold text-lg">{training.titulo}</h3>
                        {getStatusBadge(training.status)}
                        {training.tipo === 'dds' && (
                          <Badge className="bg-amber-500">DDS</Badge>
                        )}
                        {videoInfo && (
                          <Badge className="bg-red-500">
                            <Video className="w-3 h-3 mr-1" />
                            Vídeo
                          </Badge>
                        )}
                      </div>
                      
                      {training.descricao && (
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                          {training.descricao.replace(/https?:\/\/[^\s]+/g, '').trim()}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {training.carga_horaria || training.duracao_minutos || 60} min
                        </span>
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                          <Award size={12} className="mr-1" />
                          +{training.pontos || 10} pts
                        </Badge>
                      </div>
                      
                      {training.status === 'em_andamento' && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Progresso</span>
                            <span>{training.progresso || 0}%</span>
                          </div>
                          <Progress value={training.progresso || 0} className="h-2" />
                        </div>
                      )}
                    </div>
                    
                    {training.status !== 'concluido' && (
                      <Button 
                        className={`${training.tipo === 'dds' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                      >
                        <Play size={16} className="mr-2" />
                        {training.status === 'nao_iniciado' ? 'Iniciar' : 'Continuar'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <GraduationCap size={48} className="mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              Nenhum treinamento atribuído
            </h3>
            <p className="text-slate-500">
              Você não tem treinamentos pendentes no momento.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MeusTreinamentos;
